<template>
  <div
    ref="rootEl"
    :class="[ 'input-group', { 'input-group-sm': small }, 'date', { 'datetime': showTime }, { 'is-invalid': invalid } ]"
  >
    <input
      :id="dateInputId"
      ref="date"
      v-model="dateTime.date"
      class="form-control date"
      type="text"
    >
    <button
      ref="dateToggler"
      class="btn btn-picker btn-outline-secondary"
      type="button"
    >
      <span class="glyphicons calendar"/>
    </button>
    <template v-if="showTime">
      <input
        :id="timeInputId"
        ref="time"
        v-model="dateTime.time"
        autocomplete="off"
        class="form-control time ui-timepicker-input"
        type="text"
      >
      <button
        ref="timeToggler"
        class="btn btn-picker btn-outline-secondary ui-timepicker-trigger"
        type="button"
      >
        <span class="glyphicons time"/>
      </button>
    </template>
  </div>
</template>
<script lang="ts" setup>
import {computed, onMounted, onUpdated, ref, useTemplateRef, watch} from 'vue'
import $ from 'jquery'
import {isNonEmptyString} from "@/js/js-utils";

export interface DateTimeValue {
  date: string | null;
  time: string | null;
}

const model = defineModel<DateTimeValue>({required: true})

const props = defineProps({
  small: {default: false, type: Boolean},
  invalid: {default: false, type: Boolean},
  minDate: {default: null, type: String},
  showTime: {default: true, type: Boolean},
  inputIdPrefix: {default: null, type: String},
})

const rootEl = useTemplateRef<HTMLElement>('rootEl')
const dateInput = useTemplateRef<HTMLInputElement>('date')
const dateToggler = useTemplateRef<HTMLButtonElement>('dateToggler')
const timeInput = useTemplateRef<HTMLInputElement>('time')
const timeToggler = useTemplateRef<HTMLButtonElement>('timeToggler')

const dateTime = ref(model.value) // || {date: null, time: null})

const dateInputId = computed((): string => {
  return `${props.inputIdPrefix}_date`
})

const timeInputId = computed((): string => {
  return `${props.inputIdPrefix}_time`
})

watch(model, (newValue) => {
  if (newValue) {
    dateTime.value = JSON.parse(JSON.stringify(newValue))
  } else {
    dateTime.value = {
      date: null,
      time: null,
    }
  }
})

watch(
  () => props.minDate,
  (): void => {
    updateMinDate()
  }
)

onMounted(() => {
  const dateInputEl = dateInput.value
  if (dateInputEl) {
    $(dateInputEl).datepicker({
      trigger: dateToggler.value,
      autoHide: true,
      format: 'dd/mm/yyyy',
      weekStart: 1,
      zIndex: 2048, // otherwise will be hidden behind a modal
    })

    $(dateInputEl).on('change', (e) => {
      if (e.currentTarget instanceof HTMLInputElement) {
        dateTime.value.date = e.currentTarget.value
        model.value = dateTime.value
      }
    })

    const syncDateRef = rootEl.value?.dataset?.dateSyncRef
    if (syncDateRef) {
      $(dateInputEl).on("show.datepicker", function () {
        if (isNonEmptyString(dateInputEl.value)) {
          return
        }

        const fromDpInputEl = document.querySelector(`[data-date-ref="${syncDateRef}"] input.form-control.date`)
        if (fromDpInputEl instanceof HTMLInputElement && isNonEmptyString(fromDpInputEl.value)) {
          $(dateInputEl).datepicker('setViewDate', $(fromDpInputEl).datepicker('getDate'))
        }
      })
    }
  }

  const timeInputEl = timeInput.value
  if (timeInputEl) {
    $(timeInputEl).timepicker({
      timeFormat: 'H:i',
      step: 30,
      scrollDefault: '09:00',
      showOn: [],
      showOnFocus: false,
      stopScrollPropagation: true,
      wrapperHTML: '<div><div class="tp-wrapper" data-container="timepicker-list"></div></div>',
      offsetBottom: 10,
      offsetTop: -10
    })

    $(timeInputEl).on('change', (e) => {
      if (e.currentTarget instanceof HTMLInputElement) {
        dateTime.value.time = e.currentTarget.value
        model.value = dateTime.value
      }
    })

    const timeTogglerEl = timeToggler.value
    if (timeTogglerEl) {
      timeTogglerEl.addEventListener('click', function (e) {
        e.preventDefault()

        const $tpEl = $(timeInputEl)
        if ($tpEl.timepicker('isVisible')) {
          $tpEl.timepicker('hide')
        } else {
          $tpEl.timepicker('show')
        }
      })
    }
  }

  updateMinDate()
})

onUpdated((): void => {
  updateMinDate()
})

const updateMinDate = (): void => {
  const dateInputEl = dateInput.value
  if (dateInputEl) {
    $(dateInputEl).datepicker('setStartDate', props.minDate)
  }
}
</script>
